import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthObserver, AuthService } from './_core/services/auth.service';
import { CrmService } from './_core/services/crm.service';
import { UrlParams } from './_core/constants/constants';
import { ApiService } from './_core/services/api.service';
import { ProjectData } from './_core/project-data';
import { ProjectService } from './_core/services/project.service';
import { ProjectInfo } from './_core/interfaces/IProjectInfo';
import { NotifyService } from './_core/services/notify.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy, AuthObserver {
	title = 'Designer';
	isIframe = false;
	private readonly _destroying$ = new Subject<void>();

	constructor(
		private msalBroadcastService: MsalBroadcastService,
		private authService: AuthService,
		private crmService: CrmService,
		private activatedRoute: ActivatedRoute,
		private apiService: ApiService,
		private projectService: ProjectService,
		private notifyService: NotifyService,
	) { }

	ngOnInit(): void {
		this.isIframe = window !== window.parent && !window.opener;

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_START),
				takeUntil(this._destroying$)
			)
			.subscribe((msg: EventMessage) => {
				this.authService.loginStarted = true;
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS),
				takeUntil(this._destroying$)
			)
			.subscribe((msg: EventMessage) => {
				this.authService.loginSuccess(msg.payload as AuthenticationResult);
			});
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE || msg.eventType === EventType.SSO_SILENT_FAILURE),
				takeUntil(this._destroying$)
			)
			.subscribe((_: EventMessage) => {
				this.authService.loginFail();
			});

		this.authService.attach(this);
		this.authService.init();

		this.activatedRoute.queryParams.subscribe(param => {
			if (param[UrlParams.AffinityId]) {
				this.crmService.init(param[UrlParams.AffinityId]);
			}
		});
	}

	authStatusChanged(authStatus: boolean): void {
		if (!authStatus) {
			return;
		}
		const rawId = this.activatedRoute.snapshot.queryParams[UrlParams.Id];
		if (!rawId) {
			return;
		}
		let orderCode: string;
		try {
			const base64 = rawId.replace(/%3D/gi, '=');
			const ascii = atob(base64);			
			const decoded = JSON.parse(ascii);
			orderCode = decoded.orderNumber;
			if (decoded.readOnly) {
				this.projectService.readOnly = true;
			}
		} catch (error) {
			console.error('Error parsing order code from URL', error);
			this.notifyService.cfgException.emit({ 
				message: $localize`:Messages|'Wrong URL' message:Wrong link – cannot open the offer`,
				clear: false });
			return;
		}
		this.apiService.getCRMProject(orderCode).subscribe(data => {
			const offer = data.response;
			if (offer.brandName) {
				this.projectService.brandName = offer.brandName;
				if (offer.designData?.properties) {
					offer.designData.properties.brandName = offer.brandName;
				}
			}
			if (offer.designData?.properties?.productId) {
				this.projectService.productId = offer.designData.properties.productId;
			}
			const projectData: ProjectData = {
				projectInfo: new ProjectInfo({
					customerName: offer.customerName,
					orderCode: offer.code,
					externalId: offer.id.toString(),
					postalCode: offer.postalCode,
					userId: this.authService.user?.eMail,
				}),
				designerData: offer.designData				
			};
			this.crmService.loadProject(projectData);
		});
	}

	// unsubscribe to events when component is destroyed
	ngOnDestroy(): void {
		this.authService.detach(this);
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}
}
